import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';

import { PromptDiff } from '../../prompts/components/PromptDiff';
import {
    errorMessage,
    listReplays,
    PromptSource,
    REPLAY_MODEL_SUGGESTIONS,
    ReplayRun,
    startReplay,
    Verdict,
} from '../replaysApi';

import { ReplaySteps } from './ReplaySteps';
import { CheckChips, VerdictControl } from './VerdictControl';
import { GlowAlert } from '../../shared/components/GlowAlert';
import { LoadingButton } from '../../shared/components/LoadingButton';

const POLL_MS = 2000;

interface ReplayDialogProps {
    /** The reply to replay, or the message it answers. */
    messageId: string;
    onClose: () => void;
}

const RunCard: React.FC<{
    run: ReplayRun;
    onVerdict: (runId: string, verdict: Verdict | null, note: string | null) => void;
}> = ({ run, onVerdict }) => (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }} data-testid="replay-run">
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <Chip size="small" label={run.model} />
            <Chip
                size="small"
                variant="outlined"
                label={run.prompt_source === 'current' ? 'current prompts' : 'recorded prompts'}
            />
            {run.prompt_versions &&
                Object.entries(run.prompt_versions).map(([key, v]) => (
                    <Chip key={key} size="small" variant="outlined" label={`${key} v${v.version ?? '?'}`} />
                ))}
            {run.status === 'failed' && <Chip size="small" color="error" label="failed" />}
            {run.duration_ms !== null && (
                <Typography variant="caption" color="text.secondary">
                    {(run.duration_ms / 1000).toFixed(1)}s
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
                {new Date(run.created_at).toLocaleString()}
            </Typography>
        </Box>
        {run.status === 'running' && (
            <>
                <LinearProgress sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                    Running...
                </Typography>
            </>
        )}
        {run.error && <GlowAlert>{run.error}</GlowAlert>}
        {run.status === 'done' && (
            <>
                <PromptDiff
                    before={run.original_reply ?? ''}
                    after={run.reply ?? ''}
                    beforeLabel={`Original${run.original_model ? ` (${run.original_model})` : ''}`}
                    afterLabel={`Replay (${run.model})`}
                />
                <Box sx={{ mt: 1.5, display: 'grid', gap: 1 }}>
                    <CheckChips checks={run.checks} />
                    <VerdictControl run={run} onSaved={(verdict, note) => onVerdict(run.id, verdict, note)} />
                    <Typography variant="caption" color="text.secondary">
                        Tools that write were stubs, so for those the steps show what it meant to do, not what happened.
                    </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Steps
                </Typography>
                <ReplaySteps steps={run.steps} />
            </>
        )}
    </Paper>
);

/**
 * Re-runs the turn behind a reply with another model or the current prompts and shows
 * what the agent does. A replay only reads: tools that could change something are stubs.
 */
export const ReplayDialog: React.FC<ReplayDialogProps> = ({ messageId, onClose }) => {
    const [model, setModel] = useState('');
    const [promptSource, setPromptSource] = useState<PromptSource>('recorded');
    const [repeat, setRepeat] = useState(1);
    const [runs, setRuns] = useState<ReplayRun[] | null>(null);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        try {
            setRuns(await listReplays(messageId));
            setError(null);
        } catch (err) {
            setError(errorMessage(err, 'Failed to load replays'));
        }
    }, [messageId]);

    useEffect(() => {
        setRuns(null);
        refresh();
    }, [refresh]);

    const setVerdict = (runId: string, verdict: Verdict | null, note: string | null) =>
        setRuns((current) =>
            (current ?? []).map((run) => (run.id === runId ? { ...run, verdict, verdict_note: note } : run))
        );

    const running = runs?.some((run) => run.status === 'running') ?? false;
    useEffect(() => {
        if (!running) return undefined;
        const timer = setTimeout(refresh, POLL_MS);
        return () => clearTimeout(timer);
    }, [running, runs, refresh]);

    const start = async () => {
        setStarting(true);
        setError(null);
        try {
            await startReplay({ messageId, model, promptSource, repeat });
            await refresh();
        } catch (err) {
            setError(errorMessage(err, 'Failed to start the replay'));
        } finally {
            setStarting(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Replay this turn</DialogTitle>
            <DialogContent dividers>
                <GlowAlert severity="info" sx={{ mb: 2 }}>
                    A replay reads from the user's accounts but never changes anything: tools that write are
                    replaced by stubs that report what they would have done.
                </GlowAlert>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'flex-start' }}>
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={REPLAY_MODEL_SUGGESTIONS}
                        inputValue={model}
                        onInputChange={(_, value) => setModel(value)}
                        sx={{ minWidth: 320 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Model"
                                helperText="Empty: the agent's own model"
                            />
                        )}
                    />
                    <TextField
                        select
                        size="small"
                        label="Prompts"
                        value={promptSource}
                        onChange={(e) => setPromptSource(e.target.value as PromptSource)}
                        sx={{ minWidth: 200 }}
                        helperText={
                            promptSource === 'recorded' ? 'Exactly as they were' : 'As they are now'
                        }
                    >
                        <MenuItem value="recorded">Recorded</MenuItem>
                        <MenuItem value="current">Current</MenuItem>
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Runs"
                        value={repeat}
                        onChange={(e) => setRepeat(Number(e.target.value))}
                        sx={{ minWidth: 90 }}
                    >
                        {[1, 2, 3].map((n) => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </TextField>
                    <LoadingButton variant="contained" onClick={start} loading={starting || running}>
                        {running ? 'Running...' : 'Replay'}
                    </LoadingButton>
                </Box>

                {error && (
                    <GlowAlert sx={{ mb: 2 }}>
                        {error}
                    </GlowAlert>
                )}
                {runs === null && !error && <GlowAlert severity="info">Loading replays...</GlowAlert>}
                {runs?.length === 0 && (
                    <Typography color="text.secondary">No replays of this turn yet.</Typography>
                )}
                {runs?.map((run) => (
                    <RunCard key={run.id} run={run} onVerdict={setVerdict} />
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
